import { useAppSelector } from 'store/hooks';
import React from 'react';
import { MuiModal } from 'ui/MuiModal';
import { EMetricType, UserMetricResource } from '../model';
import { Button, Grid, Typography } from '@mui/material';
import { MuiForm } from 'lib/Form';
import { Form } from 'formik';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { Row } from 'ui/Flex';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { MetricTierLoyaltyForm, generateUserMetricData } from '../model/loyalty-tier';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { Option } from 'lib/types';
import { CheckboxFormField } from 'lib/form/fields/CheckboxFormField';
import * as Yup from 'yup';
import { ActionType } from 'components/actions/models/Action';
import { getActionTypeOptions } from 'components/actions/utils';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';
import { DatePickerFormField } from 'lib/form/fields/DatePickerFormField';
import { getDaysFromMinutes } from 'lib/timeHelpers';
import { AutocompleteFormField } from 'lib/form/fields/AutocompleteFormField';

interface TieredLoyaltyMetricModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (metric: UserMetricResource) => void;
    metric?: UserMetricResource;
    loading?: boolean;
    readOnly?: boolean;
    metricNames?: string[];
}

const typeOptions: Option[] = [
    { value: EMetricType.Spend, label: 'Spend' },
    { value: EMetricType.PointsEarned, label: 'Reward earned' },
    { value: EMetricType.PointsBurned, label: 'Reward burned' },
    { value: EMetricType.TipsGiven, label: 'Tips given' },
    { value: EMetricType.ActionType, label: 'Action count' }
];

export const TieredLoyaltyMetricModal: React.FC<TieredLoyaltyMetricModalProps> = ({
    open,
    onClose,
    metric,
    loading,
    onSubmit,
    readOnly,
    metricNames = []
}) => {
    const { perks } = useAppSelector(state => state.perks);
    const { settings } = useAppSelector(state => state.settings);

    const actionOptions = React.useMemo(() => {
        const allowedActions: string[] = [
            ActionType.CARD_CREATED,
            ActionType.CARD_DELETED,
            ActionType.CHECKIN,
            ActionType.USER_CREDITED,
            ActionType.ORDER_CREATED,
            ActionType.ORDER_COMPLETED,
            ActionType.REDEEM_PERK,
            ActionType.REFERAL_CLAIMED_REFEREE,
            ActionType.REFERAL_CLAIMED_REFERER,
            ActionType.REWARD_EARNED,
            ActionType.USER_CREATED,
            ActionType.USER_ACTIVATED,
            ActionType.ORDER_PAYMENT_REFUNDED,
            ActionType.ORDER_PAYMENT_RECEIVED,
            ActionType.ORDER_USER_BILLED,
            ActionType.ORDER_USER_JOINED,
            ActionType.SUBSCRIPTION_STARTED,
            ActionType.SUBSCRIPTION_PAYMENT_RECEIVED,
            ActionType.SUBSCRIPTION_PAYMENT_FAILED,
            ActionType.SUBSCRIPTION_CANCELLATION_REQUESTED,
            ActionType.SUBSCRIPTION_CANCELLED
        ];

        return getActionTypeOptions({
            includeNoneOption: false,
            subscriptionsEnabled: settings?.subscriptionsEnabled
        }).filter(option => allowedActions.includes(option.value));
    }, [settings?.subscriptionsEnabled]);

    const handleSubmit = React.useCallback(
        (values: MetricTierLoyaltyForm) => {
            const metric = generateUserMetricData(values);
            onSubmit(metric);
        },
        [onSubmit]
    );
    const validationSchema = React.useMemo(
        () =>
            Yup.object().shape({
                name: Yup.string()
                    .notOneOf(metricNames, 'Metric name must be unique.')
                    .required('This field is required.'),
                type: Yup.string().required('This field is required.'),
                timeWindowStart: Yup.number()
                    .max(366, 'This field maximum value is "366".')
                    .required('This field is required.'),
                timeWindowEnd: Yup.number().notRequired(),
                gt: Yup.date()
                    .nullable()
                    .typeError('Date is invalid.')
                    .min(new Date(), '"After" cannot be in past.'),
                lt: Yup.date()
                    .nullable()
                    .typeError('Date is invalid.')
                    .min(new Date(), '"Before" cannot be in past.'),
                perkId: Yup.string().when('type', {
                    is: (value: any) =>
                        value === EMetricType.PointsBurned || value === EMetricType.PointsEarned,
                    then: Yup.string().required('This field is required.'),
                    otherwise: undefined
                })
            }),
        [metricNames]
    );
    const initialValues: MetricTierLoyaltyForm = React.useMemo(() => {
        const metricTypeValue =
            metric?.type && actionOptions.some(item => item.value === metric.type)
                ? EMetricType.ActionType
                : metric?.type;
        return {
            name: metric?.name ?? '',
            perkId: metric?.perkId ?? '',
            includeTips: metric?.includeTips ?? false,
            timeWindowStart: metric ? getDaysFromMinutes(metric?.timeRange?.windowStartOffsetMins) : 182,
            timeWindowEnd: getDaysFromMinutes(metric?.timeRange?.windowEndOffsetMins),
            type: metricTypeValue,
            action: metricTypeValue === EMetricType.ActionType ? metric?.type : '',
            gt: metric?.timeRange?.gt,
            lt: metric?.timeRange?.lt
        };
    }, [metric, actionOptions]);
    const title = React.useMemo(() => (!!metric ? 'Metric details' : 'Create a new metric'), [metric]);
    const perkOptions = React.useMemo(
        () => perks.map(item => ({ value: item._id, label: item.title })),
        [perks]
    );
    const getTypeSpecificFields = React.useCallback(
        (type: string) => {
            switch (type) {
                case EMetricType.Spend:
                    return (
                        <Grid size={12}>
                            <CheckboxFormField name="includeTips" label="Include Tips" readOnly={readOnly} />
                        </Grid>
                    );
                case EMetricType.PointsBurned:
                case EMetricType.PointsEarned:
                    return (
                        <Grid size={12}>
                            <AutocompleteFormField
                                name="perkId"
                                label={<MuiFormLabel required>Perk</MuiFormLabel>}
                                readOnly={readOnly}
                                options={perkOptions}
                                disabled={readOnly}
                            />
                        </Grid>
                    );
                case EMetricType.ActionType:
                    return (
                        <Grid size={12}>
                            <SelectFormField
                                variant="outlined"
                                name="action"
                                label="Action"
                                readOnly={readOnly}
                                options={actionOptions}
                                disabled={readOnly}
                            />
                        </Grid>
                    );
                case EMetricType.TipsGiven:
                default:
                    return null;
            }
        },
        [actionOptions, perkOptions, readOnly]
    );
    return (
        <MuiModal open={open} onClose={onClose}>
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Typography variant="h6" color="primary">
                        {title}
                    </Typography>
                </Grid>
                <Grid size={12}>
                    <MuiForm
                        onSubmit={handleSubmit}
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                    >
                        {({ submitForm, isValid, isSubmitting, values }) => (
                            <Form>
                                <Grid container spacing={2}>
                                    <Grid size={12}>
                                        <TextFormField
                                            variant="outlined"
                                            name="name"
                                            label={<MuiFormLabel required>Name</MuiFormLabel>}
                                            readOnly={readOnly}
                                            disabled={readOnly}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <SelectFormField
                                            variant="outlined"
                                            name="type"
                                            label={<MuiFormLabel required>Type</MuiFormLabel>}
                                            readOnly={readOnly}
                                            options={typeOptions}
                                            disabled={readOnly}
                                        />
                                    </Grid>
                                    {getTypeSpecificFields(values.type)}
                                    <Grid size={12}>
                                        <Row flex={1} valign="center">
                                            <TextFormField
                                                variant="outlined"
                                                name="timeWindowStart"
                                                label={
                                                    <MuiFormLabel required>Time window start</MuiFormLabel>
                                                }
                                                type="number"
                                                readOnly={readOnly}
                                                shrink={readOnly ? true : undefined}
                                                disabled={readOnly}
                                            />
                                            <Typography sx={{ pl: 1 }}>days</Typography>
                                        </Row>
                                    </Grid>
                                    <Grid size={12}>
                                        <Row flex={1} valign="center">
                                            <TextFormField
                                                variant="outlined"
                                                name="timeWindowEnd"
                                                label="Time window end"
                                                type="number"
                                                readOnly={readOnly}
                                                shrink={readOnly ? true : undefined}
                                                disabled={readOnly}
                                            />
                                            <Typography sx={{ pl: 1 }}>days</Typography>
                                        </Row>
                                    </Grid>
                                    <Grid size={12}>
                                        <DatePickerFormField
                                            variant="outlined"
                                            name="gt"
                                            label="After"
                                            type="datetime-local"
                                            readOnly={readOnly}
                                            disabled={readOnly}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <DatePickerFormField
                                            variant="outlined"
                                            name="lt"
                                            label="Before"
                                            type="datetime-local"
                                            readOnly={readOnly}
                                            disabled={readOnly}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <Row flex={1} align="flex-end" gutter>
                                            <Button onClick={onClose} variant="outlined">
                                                Cancel
                                            </Button>
                                            {!readOnly && (
                                                <LoadingButton
                                                    disabled={!isValid}
                                                    loading={loading || isSubmitting}
                                                    onClick={submitForm}
                                                    variant="contained"
                                                >
                                                    Submit
                                                </LoadingButton>
                                            )}
                                        </Row>
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                    </MuiForm>
                </Grid>
            </Grid>
        </MuiModal>
    );
};
